import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Container,
	Paper,
	Typography,
	CircularProgress,
	Alert,
	Box,
	Button
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { AuditLogFilters } from "../components/AuditLog/AuditLogFilters";
import { AuditLogTable } from "../components/AuditLog/AuditLogTable";
import { AuditLogFilter, AuditLogEntry } from "../models/auditLog";
import {
	queryAuditLogs,
	getAuditLogs
} from "../firebase/auditLog/auditLogRepository";

const getDefaultRange = () => {
	const today = new Date();
	const sevenDaysAgo = new Date(today);
	sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
	return {
		startDate: sevenDaysAgo.toLocaleDateString("en-CA"),
		endDate: today.toLocaleDateString("en-CA")
	};
};

export const AuditLog = () => {
	const navigate = useNavigate();
	const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [filter, setFilter] = useState<AuditLogFilter>({
		...getDefaultRange(),
		entityType: "All",
		operations: ["CREATE", "UPDATE", "DELETE"],
		username: ""
	});

	const loadLogs = async () => {
		setLoading(true);
		setError(null);
		try {
			const logs = await queryAuditLogs(filter);
			setAuditLogs(logs);
		} catch (err) {
			setError("Failed to load audit logs.");
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const loadInitial = async () => {
			setLoading(true);
			setError(null);
			try {
				const logs = await getAuditLogs();
				setAuditLogs(logs);
			} catch (err) {
				setError("Failed to load audit logs.");
				console.error(err);
			} finally {
				setLoading(false);
			}
		};

		loadInitial();
	}, []);

	return (
		<Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
			<Paper elevation={3} sx={{ p: 3 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 2,
						gap: 1
					}}
				>
					<Typography variant="h5">Audit Logs</Typography>
					<Button
						variant="contained"
						startIcon={<HomeIcon />}
						onClick={() => navigate("/")}
					>
						Go to Home
					</Button>
				</Box>
				<Typography variant="body1" gutterBottom>
					Review audited Invoice and Item changes with filtering by
					date, entity, operation, and user.
				</Typography>
				<AuditLogFilters
					filter={filter}
					onFilterChange={setFilter}
					onApply={loadLogs}
					onReset={() => {
						const defaultRange = getDefaultRange();
						const resetFilter: AuditLogFilter = {
							...defaultRange,
							entityType: "All",
							operations: ["CREATE", "UPDATE", "DELETE"],
							username: ""
						};
						setFilter(resetFilter);
						queryAuditLogs(resetFilter)
							.then(setAuditLogs)
							.catch((err) => {
								console.error(err);
								setError("Failed to load audit logs.");
							});
					}}
				/>
				{loading ? (
					<Box
						sx={{ display: "flex", justifyContent: "center", p: 4 }}
					>
						<CircularProgress />
					</Box>
				) : error ? (
					<Alert severity="error">{error}</Alert>
				) : (
					<AuditLogTable auditLogs={auditLogs} />
				)}
			</Paper>
		</Container>
	);
};
